/**
 * @alfin-crm/contracts — Permission System
 *
 * All permission module keys and action keys used across API guards,
 * RBAC seeds, and the web permission matrix are defined here.
 * This is the single source of truth — never hardcode strings elsewhere.
 */

/** All CRM permission modules */
export const MODULES = {
  DASHBOARD: 'dashboard',
  USERS: 'users',
  ROLES: 'roles',
  CONTACTS: 'contacts',
  TRANSACTIONS: 'transactions',
  CONVERSATIONS: 'conversations',
  CAMPAIGNS: 'campaigns',
  TEMPLATES: 'templates',
  ASK_AI: 'ask_ai',
  MASTERS: 'masters',
  INTEGRATIONS: 'integrations',
  QUEUES: 'queues',
  AUDIT: 'audit',
  AI_USAGE: 'ai_usage',
} as const;

export type ModuleKey = (typeof MODULES)[keyof typeof MODULES];

/** All permission actions */
export const ACTIONS = {
  VIEW: 'VIEW',
  ADD: 'ADD',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  APPROVE: 'APPROVE',
  EXPORT: 'EXPORT',
  SPECIAL: 'SPECIAL',
} as const;

export type ActionKey = (typeof ACTIONS)[keyof typeof ACTIONS];

export type Permission = `${ModuleKey}:${ActionKey}`;

/** Build a typed permission string */
export const perm = (module: ModuleKey, action: ActionKey): Permission =>
  `${module}:${action}` as Permission;

/**
 * Role preset permission grants.
 * Used for seeding and client-side role matrix display.
 */
export type RolePreset = {
  [M in ModuleKey]?: ActionKey[];
};

export const SUPER_ADMIN_PRESET: RolePreset = Object.fromEntries(
  Object.values(MODULES).map((m) => [m, Object.values(ACTIONS)]),
) as RolePreset;

export const ADMIN_PRESET: RolePreset = {
  dashboard: ['VIEW', 'EXPORT'],
  conversations: ['VIEW', 'ADD', 'EDIT', 'EXPORT'],
  contacts: ['VIEW', 'ADD', 'EDIT', 'DELETE', 'EXPORT'],
  campaigns: ['VIEW', 'ADD', 'EDIT', 'APPROVE', 'EXPORT'],
  templates: ['VIEW', 'ADD', 'EDIT', 'APPROVE', 'EXPORT'],
  transactions: ['VIEW', 'ADD', 'EDIT', 'APPROVE', 'EXPORT'],
  ask_ai: ['VIEW', 'EXPORT'],
  masters: ['VIEW'],
  users: ['VIEW', 'ADD', 'EDIT'],
  roles: ['VIEW'],
  audit: ['VIEW'],
  ai_usage: ['VIEW', 'EXPORT'],
};

/** Team lead — manages staff under them */
export const MANAGER_PRESET: RolePreset = {
  dashboard: ['VIEW', 'EXPORT'],
  conversations: ['VIEW', 'ADD', 'EDIT', 'EXPORT'],
  contacts: ['VIEW', 'ADD', 'EDIT', 'EXPORT'],
  campaigns: ['VIEW', 'ADD', 'EDIT'],
  templates: ['VIEW'],
  transactions: ['VIEW', 'ADD', 'EDIT'],
  ask_ai: ['VIEW', 'EXPORT'],
  masters: ['VIEW'],
  users: ['VIEW'],
};

/** Front-line agent — own records only */
export const STAFF_PRESET: RolePreset = {
  dashboard: ['VIEW'],
  conversations: ['VIEW', 'ADD', 'EDIT'],
  contacts: ['VIEW', 'ADD', 'EDIT'],
  campaigns: ['VIEW'],
  templates: ['VIEW'],
  transactions: ['VIEW', 'ADD'],
  ask_ai: ['VIEW'],
};

/** @deprecated Use STAFF_PRESET — kept for older imports */
export const USER_PRESET = STAFF_PRESET;

/** Built-in role names */
export const ROLE_NAMES = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  STAFF: 'Staff',
} as const;

export type RoleName = (typeof ROLE_NAMES)[keyof typeof ROLE_NAMES];

/** Roles visible in the admin panel (excludes Super Admin) */
export const PANEL_ROLE_NAMES = [
  ROLE_NAMES.ADMIN,
  ROLE_NAMES.MANAGER,
  ROLE_NAMES.STAFF,
] as const;
